import React, { Suspense, useEffect } from 'react';
import { WhatsAppFloat } from './components/WhatsApp.jsx';
import { ActivityStripElsewhere } from './components/PriceTicker.jsx';
import WhatsAppInbox from './pages/workspace/WhatsAppInbox.jsx';
import { lazyWithReload } from './lazyWithReload.js';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { RequireAdmin, RequireAuth } from './auth.jsx';
import { useI18n } from './i18n/index.jsx';
import Header from './components/Header.jsx';
import LangBar, { useUrlLang } from './components/LangBar.jsx';
import ImpersonationBanner from './components/ImpersonationBanner.jsx';
import Footer from './components/Footer.jsx';
import Home from './pages/site/Home.jsx';
import NotFound from './pages/site/NotFound.jsx';
import About from './pages/site/About.jsx';
import Team from './pages/site/Team.jsx';
import News from './pages/site/News.jsx';
import NewsPost from './pages/site/NewsPost.jsx';
import { DepartmentPage, ProductTypePage, SectorPage, SubCategoryPage } from './pages/catalogue/CataloguePages.jsx';
import { ContentProvider, PreviewBanner } from './content.jsx';
import Prices from './pages/site/Prices.jsx';
import Contact from './pages/site/Contact.jsx';
import Help from './pages/site/Help.jsx';
import Marketplace from './pages/marketplace/Marketplace.jsx';
import ListingDetail from './pages/marketplace/ListingDetail.jsx';
import SellerProfile from './pages/marketplace/SellerProfile.jsx';
import ListingForm from './pages/member/ListingForm.jsx';
import RequestDetail from './pages/requests/RequestDetail.jsx';
import RequestForm from './pages/requests/RequestForm.jsx';
import MyRequests from './pages/requests/MyRequests.jsx';
import MyRequestDetail from './pages/requests/MyRequestDetail.jsx';
import AccountLayout, { SettingsIndex, AccountIndex, Guard } from './pages/account/AccountLayout.jsx';
import TeamJoin from './pages/auth/TeamJoin.jsx';
import DocView from './pages/site/DocView.jsx';
import ShowcasePublic from './pages/site/ShowcasePublic.jsx';
import UnsubscribePage from './pages/site/UnsubscribePage.jsx';
import TeamPage, { ActivityLog } from './pages/workspace/TeamPage.jsx';
import WorkspaceHome from './pages/workspace/WorkspaceHome.jsx';
import Customers from './pages/workspace/Customers.jsx';
import CustomerPage from './pages/workspace/CustomerPage.jsx';
import Tasks from './pages/workspace/Tasks.jsx';
import { DocumentsList, DocumentNew, DocumentEdit, DocumentPage } from './pages/workspace/Documents.jsx';
import { DocTemplates, DocLetterhead } from './pages/workspace/DocLibrary.jsx';
import { Stats, RepRequests } from './pages/workspace/WorkspaceMore.jsx';
import DashboardHome from './pages/account/DashboardHome.jsx';
import ProductsPage from './pages/account/ProductsPage.jsx';
import RequestsPage from './pages/account/RequestsPage.jsx';
import CompanyPage from './pages/account/CompanyPage.jsx';
import ContactPage from './pages/account/ContactPage.jsx';
import DocumentsPage from './pages/account/DocumentsPage.jsx';
import PlanPage from './pages/account/PlanPage.jsx';
import SecurityPage from './pages/account/SecurityPage.jsx';
import { MySignaturePage } from './pages/workspace/Signatures.jsx';
import MailboxPage from './pages/account/MailboxPage.jsx';
import MessagesScreen from './chat/MessagesScreen.jsx';
import { Privacy, Terms } from './pages/site/LegalPage.jsx';
import Signup from './pages/auth/Signup.jsx';
import Login from './pages/auth/Login.jsx';
import ForgotPassword from './pages/auth/ForgotPassword.jsx';
import VerifyEmail from './pages/auth/VerifyEmail.jsx';
import CompleteProfile from './pages/auth/CompleteProfile.jsx';

const AdminApp = lazyWithReload(() => import('./pages/admin/AdminApp.jsx'));

function ScrollToTop() {
  const { pathname } = useLocation();
  // Braces matter: current Chrome returns a Promise from window.scrollTo,
  // and an effect that returns it makes React call the Promise as its
  // cleanup on the next navigation — "destroy is not a function", blank page.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// /fr/… and /en/… links: the language is applied (unless one was picked),
// then the page opens at its normal address.
function LangPrefix() {
  const { '*': rest = '' } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/${rest}${search}${hash}`} replace />;
}

// /mon-compte, /mon-compte/produits… → the same page under /account.
const FR_ACCOUNT = { '': 'dashboard', 'tableau-de-bord': 'dashboard', produits: 'products', 'ajouter-un-produit': 'listings/new', demandes: 'requests', 'demandes-achat': 'buying-requests', entreprise: 'company',
  'profil-entreprise': 'company', coordonnees: 'contact', documents: 'documents', abonnement: 'plan', forfait: 'plan', securite: 'security', parametres: 'settings' };
function MonCompte() {
  const { '*': rest = '' } = useParams();
  const { search, hash } = useLocation();
  const [first, ...more] = rest.split('/');
  const to = FR_ACCOUNT[first] ? [FR_ACCOUNT[first], ...more].filter(Boolean).join('/') : rest;
  return <Navigate to={`/account/${to}${search}${hash}`} replace />;
}

function SiteLayout() {
  useUrlLang();
  return (
    <ContentProvider>
      <div className="site">
        <ImpersonationBanner />
        <PreviewBanner />
        <Header />
        <LangBar />
        <ActivityStripElsewhere />
        <main className="site-main"><Outlet /></main>
        <Footer />
        <WhatsAppFloat />
      </div>
    </ContentProvider>
  );
}

function Loading() {
  const { t } = useI18n();
  return <div className="page-loading">{t('common.loading')}</div>;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/admin/*" element={
          <RequireAdmin>
            <Suspense fallback={<Loading />}><AdminApp /></Suspense>
          </RequireAdmin>
        } />
        <Route element={<SiteLayout />}>
          <Route index element={<Home />} />
          <Route path="marketplace" element={<Marketplace />} />
          <Route path="marketplace/requests/:id" element={<RequestDetail />} />
          <Route path="marketplace/:id" element={<ListingDetail />} />
          <Route path="sellers/:slug" element={<SellerProfile />} />
          <Route path="about" element={<About />} />
          <Route path="equipe" element={<Team />} />
          <Route path="team" element={<Team />} />
          <Route path="news" element={<News />} />
          <Route path="news/:slug" element={<NewsPost />} />
          {/* Catalogue: French and English addresses. */}
          <Route path="secteurs/:sector" element={<SectorPage />} />
          <Route path="sectors/:sector" element={<SectorPage />} />
          <Route path="secteurs/:sector/:dept" element={<DepartmentPage />} />
          <Route path="secteurs/:sector/:dept/:sub" element={<SubCategoryPage />} />
          <Route path="secteurs/:sector/:dept/:sub/:type" element={<ProductTypePage />} />
          <Route path="sectors/:sector/:dept" element={<DepartmentPage />} />
          <Route path="sectors/:sector/:dept/:sub" element={<SubCategoryPage />} />
          <Route path="sectors/:sector/:dept/:sub/:type" element={<ProductTypePage />} />
          <Route path="prices" element={<Prices />} />
          <Route path="contact" element={<Contact />} />
          <Route path="help" element={<Help />} />
          <Route path="aide" element={<Help />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<Terms />} />
          <Route path="signup" element={<Signup />} />
          <Route path="inscription" element={<Signup />} />
          {/* Old emailed links (before 6-digit codes) land on the new flows. */}
          <Route path="signup/complete" element={<Navigate to="/signup" replace />} />
          <Route path="login" element={<Login />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<Navigate to="/forgot-password" replace />} />
          <Route path="verify-email" element={<VerifyEmail />} />
          {/* Company users (migration 030): invitation, documents sent to customers. */}
          <Route path="team/join" element={<TeamJoin />} />
          <Route path="d/:token" element={<DocView />} />
          {/* "My customers" (migration 035): shared showcase, unsubscribe — no sign-in. */}
          <Route path="showcase/:token" element={<ShowcasePublic />} />
          <Route path="unsubscribe/:token" element={<UnsubscribePage />} />
          <Route path="unsubscribe/m/:token" element={<UnsubscribePage member />} />
          <Route path="unsubscribe/r/:token" element={<UnsubscribePage alerts />} />
          {/* My account: side panel + pages, each with its own address. */}
          <Route path="account/complete-profile" element={<RequireAuth><CompleteProfile /></RequireAuth>} />
          <Route path="account" element={<RequireAuth><AccountLayout /></RequireAuth>}>
            <Route index element={<AccountIndex />} />
            <Route path="dashboard" element={<Guard perm="products.view"><DashboardHome /></Guard>} />
            <Route path="products" element={<Guard perm="products.view|products.create"><ProductsPage /></Guard>} />
            <Route path="listings/new" element={<Guard perm="products.create"><ListingForm /></Guard>} />
            <Route path="listings/:id/edit" element={<Guard perm="products.edit|products.create"><ListingForm /></Guard>} />
            <Route path="requests" element={<Guard perm="rfq.viewAll|rfq.viewAssigned"><RequestsPage /></Guard>} />
            <Route path="requests/:id" element={<Guard perm="rfq.viewAll|rfq.viewAssigned"><RequestsPage /></Guard>} />
            <Route path="buying-requests" element={<MyRequests />} />
            <Route path="buying-requests/new" element={<RequestForm />} />
            <Route path="buying-requests/:id" element={<MyRequestDetail />} />
            <Route path="buying-requests/:id/edit" element={<RequestForm />} />
            <Route path="messages" element={<Guard perm="chat.own|chat.all"><MessagesScreen linkBase="/account/messages" /></Guard>} />
            <Route path="messages/:id" element={<Guard perm="chat.own|chat.all"><MessagesScreen linkBase="/account/messages" /></Guard>} />
            <Route path="company" element={<Guard perm="profile.view"><CompanyPage /></Guard>} />
            <Route path="contact" element={<Guard perm="profile.view"><ContactPage /></Guard>} />
            <Route path="documents" element={<Guard perm="profile.view"><DocumentsPage /></Guard>} />
            <Route path="plan" element={<Guard perm="billing.view"><PlanPage /></Guard>} />
            {/* Company users & "Mon espace" (migration 030) */}
            <Route path="team" element={<TeamPage />} />
            <Route path="team/activity" element={<ActivityLog />} />
            <Route path="workspace" element={<WorkspaceHome />} />
            <Route path="workspace/customers" element={<Customers />} />
            <Route path="workspace/customers/:id" element={<CustomerPage />} />
            <Route path="workspace/tasks" element={<Tasks />} />
            <Route path="workspace/documents" element={<DocumentsList />} />
            <Route path="workspace/documents/new" element={<DocumentNew />} />
            <Route path="workspace/documents/:id" element={<DocumentPage />} />
            <Route path="workspace/documents/:id/edit" element={<DocumentEdit />} />
            <Route path="workspace/stats" element={<Stats />} />
            <Route path="workspace/templates" element={<DocTemplates />} />
            <Route path="workspace/templates/letterhead" element={<DocLetterhead />} />
            <Route path="workspace/requests" element={<RepRequests />} />
            <Route path="workspace/whatsapp" element={<WhatsAppInbox />} />
            <Route path="workspace/messages" element={<MessagesScreen team apiBase="/workspace/chat" linkBase="/account/workspace/messages" />} />
            <Route path="workspace/messages/:id" element={<MessagesScreen team apiBase="/workspace/chat" linkBase="/account/workspace/messages" />} />
            <Route path="security" element={<SecurityPage />} />
            <Route path="mailbox" element={<MailboxPage />} />
            <Route path="signature" element={<MySignaturePage />} />
            <Route path="settings" element={<SettingsIndex />} />
            {/* Older addresses */}
            <Route path="membership" element={<Navigate to="/account/plan" replace />} />
            <Route path="listings" element={<Navigate to="/account/products" replace />} />
            <Route path="profile" element={<Navigate to="/account/company" replace />} />
            <Route path="*" element={<Navigate to="/account/dashboard" replace />} />
          </Route>
          {/* French addresses (/mon-compte/…) */}
          <Route path="mon-compte/*" element={<MonCompte />} />
          <Route path="fr/*" element={<LangPrefix />} />
          <Route path="en/*" element={<LangPrefix />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
}
